import { PagingResponse } from '@/@types/responses/common/PagingResponse'
import { Manufacturer } from '@/@types/responses/manufacturer/Manufacturer'
import { ManufacturerDetails } from '@/@types/responses/manufacturer/ManufacturerDetails'
import { AppConfig } from '@/app.config'
import authenticatedFetch from '@/authenticatedFetch'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import { DropdownOptionsResponse } from '@/@types/responses/common/DropdownOptionsResponse'
import { ManufacturerMessage } from '@/@types/messages/ManufacturerMessage'
import { ContactMessage } from '@/@types/messages/ContactMessage'
import { MiscellaneousService } from './miscellaneous.service'
import { ObjectCreatedResponse } from '@/@types/responses/common/ObjectCreatedResponse'

export class ManufacturerService {
  private appConfig: AppConfig
  private miscellaneousService: MiscellaneousService

  constructor(miscellaneousService: MiscellaneousService) {
    this.appConfig = new AppConfig()
    this.miscellaneousService = miscellaneousService
  }

  public createManufacturer(manufacturerMessage: ManufacturerMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(manufacturerMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/manufacturers`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public createManufacturerContact(manufacturerId: number, contactMessage: ContactMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(contactMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/manufacturers/${manufacturerId}/contacts`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public deleteManufacturer(id: number): Promise<void> {
    const requestOptions = {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/manufacturers/${id}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public getManufacturer(id: number): Promise<ManufacturerDetails> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/manufacturers/${id}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return new ManufacturerDetails(json.id, json.name, json.website, json.email, json.phoneNumber, json.addressLine1, json.addressLine2, json.city, json.stateId, json.postalCode, json.contacts, json.createdByUser, json.createdDate, json.lastModifiedByUser, json.lastModifiedDate)
    })
  }

  public getManufacturerDropdownOptions(): Promise<DropdownOptionsResponse<DropdownOptionResponse<number>>> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/manufacturers/dropdown-options`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return json as DropdownOptionsResponse<DropdownOptionResponse<number>>
    })
  }

  public searchManufacturers(searchTerm: string, pageNumber: number, pageSize: number, sortField: string, ascending: boolean): Promise<PagingResponse<Manufacturer>> {
    const queryString = `?searchTerm=${searchTerm}&pageNumber=${pageNumber}&pageSize=${pageSize}&sortField=${sortField}&ascending=${ascending}`

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/manufacturers${queryString}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return json as PagingResponse<Manufacturer>
    })
  }

  public updateManufacturer(manufacturerId: number, manufacturerMessage: ManufacturerMessage): Promise<void> {
    const requestOptions = {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(manufacturerMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/manufacturers/${manufacturerId}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }
}
